import { PaymentMethodInterface } from './PaymentMethodInterface'
import { SubscriptionPlanInterface } from './SubscriptionPlanInterface'

export enum QuotaTypes {
  ACCOUNTANT_ACCESS = 'accounting_access',
  AI_EXPENSES_CATEGORIZATION = 'AI_expenses_categorization',
  AUTO_BANK_RECONCILIATION = 'auto_bank_reconciliation',
  BANK_RECONCILIATION = 'bank_reconciliation',
  BANKING = 'banking',
  BOOKKEEPING_SUPPORT = 'bookkeeping_support',
  CUSTOM_INVOICE_STYLING = 'custom_invoice_styling',
  CUSTOMERS = 'premium_customers',
  DOWNPAYMENT = 'down_payment',
  E_INVOICE = 'e_invoice',
  EMAIL_ATTACHMENT = 'email_attachment',
  ESTIMATES = 'premium_estimate_to_invoice',
  EXPENSES = 'expenses',
  FREE_EMAIL = 'free_email',
  FREE_ESTIMATES = 'free_estimates',
  FREE_INVOICE_LIMIT = 'free_invoice_limit',
  FREE_INVOICING = 'free_invoice',
  FREE_MOBILE = 'free_mobile',
  FREE_PRODUCTS = 'free_products',
  FREE_REPORTING = 'free_reports',
  INVOICE = 'premium_invoice',
  INVOICES_ESTIMATES = 'invoices_estimates',
  LIMITED_INVOICES_ESTIMATES = 'limited_invoices_estimates',
  ONLINE_BANK_PAYMENTS = 'tpayment_link',
  ONLINE_CARD_PAYMENTS = 'online_card_payments',
  PAYMENT_SCHEDULE = 'premium_payment_schedule',
  RECEIPT_SCANNING = 'receipt_scanning',
  RECURRING_PROFILE = 'recurring_profile',
  REMINDERS = 'premium_reminders',
  TIMESHEET = 'premium_timesheet',
  TRADENAMES = 'premium_tradenames',
  VAT_RETURNS = 'VAT_returns',
}

export enum VatRateTypes {
  FI = 0.255,
  DK = 0.25,
}

// Defines minimum free quota for a premium feature when customer has no subscription.
export enum DefaultQuotaWhenNoSubscription {
  CUSTOMERS = 10,
  FREE_INVOICE_LIMIT = 5,
}

export enum TinkFreeConsumablesLimit {
  MAX_FREE = 1,
}

export enum ConsumablesType {
  TINK_PAYMENT = 'tink_payment',
}

export enum ConsumablesBillingStatus {
  UNPAID = 'unpaid',
  FREE = 'free',
  INVOICED = 'invoiced',
  PAID = 'paid',
}

export type SubscriptionStatus =
  | 'trialing'
  | 'active'
  | 'past_due'
  | 'canceled'
  | 'no_subscription'
  | 'unpaid'
  | 'free'
  | 'incomplete'

export enum SEPAStatus {
  available = 'available',
  warning = 'warning',
  blocked = 'blocked',
}

export enum StripeVatValidation {
  pending = 'pending',
  unavailable = 'unavailable',
  unverified = 'unverified',
  verified = 'verified',
}

export interface SubscriptionInterface {
  readonly id: string
  readonly status: SubscriptionStatus
  readonly plan: SubscriptionPlanInterface
  readonly created: number
  readonly preferredLocales?: string[]
  readonly payment: PaymentMethodInterface
  readonly quotas: UsedQuotaInterface[]
  readonly consumables?: ConsumablesCounterInterface
  readonly inDowngrade?: boolean
  readonly downgradeAt?: number
  readonly currentPeriodStart?: number
  readonly currentPeriodEnd: number
  readonly downgradeTo?: string
  readonly downgradeId?: string
  readonly referralIdUsed?: string | boolean
  readonly default_tax_rate?: number
  readonly churnDiscount?: {
    percent_off: number
    amount_off: number
    available: boolean
    description: string
    churnDiscountUsed: boolean
  }
  readonly switchStripeAccount?: boolean
  readonly sepaDirectDebitStatus: SEPAStatus
  readonly daysPastDue?: number
  readonly stripeVatValidation?: StripeVatValidation
}

export interface UsedQuotaInterface {
  readonly type: QuotaTypes
  readonly total: number
  readonly used: number
  readonly validFrom: number
  readonly validTo: number
}

export interface ConsumablesCounterInterface {
  companyId: number
  nextInvoicingDate: string
  [ConsumablesType.TINK_PAYMENT]: {
    [ConsumablesBillingStatus.UNPAID]: number
    [ConsumablesBillingStatus.FREE]: number
    [ConsumablesBillingStatus.INVOICED]: number
    [ConsumablesBillingStatus.PAID]: number
  }
}

export interface SubscriptionInvoiceInterface {
  recipeNumber: string
  from: Date
  to: Date
  total: number
  tax?: number
  taxPercentage: number
  items: InvoiceItemInterface[]
  paid: boolean
  pdf_url: string
  view_url: string
  discount?: string
}

export interface InvoiceItemInterface {
  amount: number
  currency: string
  description: string
}
